import {
  FiClock,
  FiCalendar,
  FiDollarSign,
  FiCheckCircle,
  FiUserPlus,
  FiArrowRight,
  FiActivity,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const iconMap = {
  ATTENDANCE: FiClock,
  LEAVE: FiCalendar,
  PAYROLL: FiDollarSign,
  CORRECTION: FiCheckCircle,
  EMPLOYEE: FiUserPlus,
  DEFAULT: FiActivity,
};

const colorMap = {
  ATTENDANCE: "bg-gradient-to-br from-blue-400 to-blue-600",
  LEAVE: "bg-gradient-to-br from-orange-400 to-amber-500",
  PAYROLL: "bg-gradient-to-br from-emerald-400 to-green-600",
  CORRECTION: "bg-gradient-to-br from-purple-400 to-violet-600",
  EMPLOYEE: "bg-gradient-to-br from-pink-400 to-rose-500",
  DEFAULT: "bg-gradient-to-br from-gray-400 to-gray-600",
};

const activityPathMap = {
  ATTENDANCE: "/admindashboard/attendance",
  LEAVE: "/admindashboard/leave",
  PAYROLL: "/admindashboard/payroll",
  CORRECTION: "/admindashboard/attendance/corrections",
  EMPLOYEE: "/admindashboard/employees",
};

export default function ActivityTimeline({
  activities = [],
  isLoading = false,
  footerPath,
  footerLabel,
}) {
  const navigate = useNavigate();

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm transition-shadow duration-300 hover:shadow-md">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Recent Activities
          </h2>

          <p className="mt-1 text-sm text-gray-500">
            Track important HR and business activities.
          </p>
        </div>

        <div className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-600">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          Live
        </div>
      </div>

      <div className="space-y-1">
        {isLoading ? (
          <div className="py-16 text-center text-sm text-gray-400">
            Loading activities…
          </div>
        ) : activities.length ? (
          activities.slice(0, 7).map((activity, index) => {
            const type = activity.type || "DEFAULT";
            const Icon = iconMap[type] || iconMap.DEFAULT;

            return (
              <button
                key={activity.id}
                type="button"
                onClick={() => {
                  const path = activityPathMap[type];
                  if (path) navigate(path);
                }}
                className="group -mx-2 flex gap-4 rounded-2xl px-2 py-2 transition-colors duration-200 hover:bg-gray-50"
              >
                <div className="flex flex-col items-center">
                  <div
                    className={`flex h-11 w-11 items-center justify-center rounded-xl text-white transition-transform duration-300 group-hover:scale-105 ${
                      colorMap[type] || colorMap.DEFAULT
                    }`}
                  >
                    <Icon size={18} />
                  </div>

                  {index !== activities.length - 1 ? (
                    <div className="mt-2 w-0.5 flex-1 rounded-full bg-gray-100" />
                  ) : null}
                </div>

                <div className="flex-1 pb-3">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-medium text-gray-900">
                        {activity.title}
                      </h3>

                      <p className="mt-1 text-sm text-gray-500">
                        {activity.description}
                      </p>
                    </div>

                    <span className="whitespace-nowrap rounded-full bg-gray-50 px-2.5 py-1 text-xs font-medium text-gray-400">
                      {activity.timeLabel}
                    </span>
                  </div>
                </div>
              </button>
            );
          })
        ) : (
          <div className="py-16 text-center text-sm text-gray-400">
            No recent activities found.
          </div>
        )}
      </div>

      {footerPath ? (
        <button
          type="button"
          onClick={() => navigate(footerPath)}
          className="group mt-2 flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 py-3 text-sm font-semibold text-gray-600 transition-all duration-300 hover:border-gray-900 hover:bg-gray-900 hover:text-white"
        >
          {footerLabel || "View details"}
          <FiArrowRight className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      ) : null}
    </div>
  );
}
